/**
 * @file BackupTokenRotateDialog.tsx
 * @description Плановая замена ключа доступа агента бэкапа (phase19.26 шаг 7;
 * шаг 7.1 — ключ только маской, Р3).
 *
 * Два шага в одном диалоге: подтверждение → результат. Новый ключ панель не
 * получает и не показывает: агент заберёт его из `agent-config` при следующем
 * запуске, подтвердит, и только тогда старый погаснет. От утечки старого ключа
 * плановая замена не защищает — для этого «Экстренный отзыв»
 * (`BackupTokenRevokeDialog`).
 */

import { useState } from "react";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { Button } from "../../ui/button";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { useNotify } from "../../ui/NotifyProvider";
import { backupErrorMessage, rotateBackupWebhookToken } from "../../../lib/api-backups";

/** Что будет при плановой замене — до подтверждения. */
const ROTATE_CONFIRM_TEXT =
  "Будет создан новый ключ. Агент получит его сам при следующем запуске бэкапа (обычно ночью, или " +
  "«Запустить сейчас») и подтвердит; до этого действует старый. Заходить на сервер не нужно.";

/** Итог по ответу сервера: ждёт агента или действует сразу (агента ещё не было). */
const RESULT_TEXT = {
  pending:
    "Новый ключ создан. Агент получит его сам при следующем запуске и подтвердит; до этого действует старый.",
  immediate:
    "Новый ключ создан и действует сразу: агент ещё не был подключён. Выполните команду установки ниже.",
} as const;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Ключ выпущен — перечитать сведения (появится «ждёт подтверждения агентом»). */
  onRotated: () => void;
}

export function BackupTokenRotateDialog({ open, onClose, onRotated }: Props) {
  const notify = useNotify();
  // null — ещё не меняли; иначе — какой текст результата показать.
  const [result, setResult] = useState<keyof typeof RESULT_TEXT | null>(null);
  const [busy, setBusy] = useState(false);

  function close() {
    setResult(null);
    onClose();
  }

  async function rotate() {
    setBusy(true);
    try {
      const res = await rotateBackupWebhookToken();
      setResult(res.pending ? "pending" : "immediate");
      onRotated();
    } catch (err) {
      notify.error(backupErrorMessage(err, "Не удалось создать новый ключ"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={(o) => (!o && !busy ? close() : null)}>
      <AlertDialogContent fullScreen className="max-w-lg gap-3">
        <AlertDialogHeader>
          <AlertDialogTitle>{result ? "Новый ключ создан" : "Плановая замена ключа"}</AlertDialogTitle>
          <AlertDialogDescription>{result ? RESULT_TEXT[result] : ROTATE_CONFIRM_TEXT}</AlertDialogDescription>
        </AlertDialogHeader>

        {!result ? (
          <NoticeStrip tone="warning">
            Плановая замена не защищает, если старый ключ попал к посторонним — для этого есть
            «Экстренный отзыв».
          </NoticeStrip>
        ) : null}

        <AlertDialogFooter>
          {result ? (
            <Button onClick={close}>
              Готово
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={close} disabled={busy}>
                Не сейчас
              </Button>
              {/* Замена ключа — с последствиями, но обратимая: янтарный. */}
              <Button variant="warning" onClick={() => void rotate()} loading={busy}>
                Заменить ключ
              </Button>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
